import { useState, useEffect, useMemo } from 'react';
import {
  listMeters, createMeter, updateMeter, deleteMeter,
  listMeterReadings, createMeterReading, updateMeterReading, verifyMeterReading,
  deleteMeterReading, getMeterConsumption,
  getProperties, getUnits, getTenants,
} from '../services/api';
import {
  Gauge, Plus, PencilSimple, Trash, X, Warning, Check, CheckCircle,
  Lightning, Drop, Fire, Flame, Wrench, Calculator,
} from '@phosphor-icons/react';

const METER_TYPE_LABELS = {
  strom: { label: 'Strom', icon: Lightning, defaultUnit: 'kWh' },
  wasser_kalt: { label: 'Wasser kalt', icon: Drop, defaultUnit: 'm³' },
  wasser_warm: { label: 'Wasser warm', icon: Drop, defaultUnit: 'm³' },
  heizung: { label: 'Heizung', icon: Fire, defaultUnit: 'kWh' },
  gas: { label: 'Gas', icon: Flame, defaultUnit: 'm³' },
  custom: { label: 'Sonstiger', icon: Wrench, defaultUnit: '' },
};

const REASON_LABELS = {
  regular: 'Regulär',
  einzug: 'Einzug',
  auszug: 'Auszug',
  jahresende: 'Jahresende',
};

const STATUS_BADGES = {
  submitted: { label: 'Eingegangen', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  verified: { label: 'Geprüft', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-800 border-red-200' },
};

function eur(n) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

export default function Zaehler() {
  const [meters, setMeters] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [filterProperty, setFilterProperty] = useState('');
  const [filterUnit, setFilterUnit] = useState('');
  const [loading, setLoading] = useState(true);
  const [showMeterModal, setShowMeterModal] = useState(false);
  const [editingMeter, setEditingMeter] = useState(null);
  const [openReadings, setOpenReadings] = useState(null); // meter object

  const loadData = async () => {
    try {
      const [mRes, pRes, uRes, tRes] = await Promise.all([
        listMeters(),
        getProperties(),
        getUnits(),
        getTenants(),
      ]);
      setMeters(mRes.data);
      setProperties(pRes.data);
      setUnits(uRes.data);
      setTenants(tRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const unitsForFilter = useMemo(
    () => filterProperty ? units.filter(u => u.property_id === filterProperty) : units,
    [units, filterProperty]
  );

  const filteredMeters = useMemo(() => {
    return meters.filter(m => {
      if (filterUnit && m.unit_id !== filterUnit) return false;
      if (filterProperty) {
        const u = units.find(uu => uu.id === m.unit_id);
        if (!u || u.property_id !== filterProperty) return false;
      }
      return true;
    });
  }, [meters, filterUnit, filterProperty, units]);

  const handleDelete = async (m) => {
    if (!window.confirm(`Zähler "${m.label}" wirklich löschen? Alle Ablesungen werden ebenfalls entfernt.`)) return;
    try { await deleteMeter(m.id); loadData(); }
    catch (e) { alert('Fehler: ' + (e.response?.data?.detail || e.message)); }
  };

  const unitLabel = (unitId) => {
    const u = units.find(uu => uu.id === unitId);
    if (!u) return '—';
    const p = properties.find(pp => pp.id === u.property_id);
    return `${u.name} (${p?.name || ''})`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="zaehler-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900">Zähler</h1>
          <p className="text-slate-500 mt-1 text-sm">Strom, Wasser, Heizung — Ablesungen verwalten und prüfen</p>
        </div>
        <button
          onClick={() => { setEditingMeter(null); setShowMeterModal(true); }}
          className="bg-slate-900 text-white px-5 py-2.5 font-medium hover:bg-slate-800 inline-flex items-center gap-2 rounded-lg"
          data-testid="add-meter-button"
        >
          <Plus weight="bold" className="w-4 h-4" /> Zähler hinzufügen
        </button>
      </div>

      {/* Filter */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col sm:flex-row gap-3">
        <select
          value={filterProperty}
          onChange={(e) => { setFilterProperty(e.target.value); setFilterUnit(''); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white flex-1"
          data-testid="filter-property"
        >
          <option value="">Alle Objekte</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select
          value={filterUnit}
          onChange={(e) => setFilterUnit(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white flex-1"
          data-testid="filter-unit"
        >
          <option value="">Alle Einheiten</option>
          {unitsForFilter.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {/* Meters list */}
      {filteredMeters.length === 0 ? (
        <div className="bg-white border border-slate-200 p-10 text-center rounded-lg">
          <Gauge weight="light" className="w-14 h-14 text-slate-300 mx-auto mb-3" />
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">Keine Zähler vorhanden</h3>
          <p className="text-slate-500 text-sm">Lege deinen ersten Zähler an, um Ablesungen zu erfassen.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-sm" data-testid="meters-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Zähler</th>
                <th className="text-left px-4 py-3">Einheit</th>
                <th className="text-left px-4 py-3">Typ</th>
                <th className="text-left px-4 py-3">Faktor</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredMeters.map(m => {
                const meta = METER_TYPE_LABELS[m.meter_type] || METER_TYPE_LABELS.custom;
                const Icon = meta.icon;
                return (
                  <tr key={m.id} className="hover:bg-slate-50" data-testid={`meter-row-${m.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <Icon weight="bold" className="w-5 h-5 text-slate-600" />
                        <div>
                          <p className="font-medium text-slate-900">{m.label}</p>
                          {m.serial_number && <p className="text-xs text-slate-500">Nr. {m.serial_number}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{unitLabel(m.unit_id)}</td>
                    <td className="px-4 py-3 text-slate-700">{meta.label} <span className="text-slate-400">({m.unit_of_measure})</span></td>
                    <td className="px-4 py-3 font-mono text-slate-700">
                      {m.conversion_factor != null ? `${m.conversion_factor} €/${m.unit_of_measure}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        <span className={`text-xs px-2 py-0.5 rounded border ${m.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                          {m.active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded border ${m.paid_by_tenant ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                          {m.paid_by_tenant ? 'Mieter zahlt' : 'Vermieter zahlt'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setOpenReadings(m)}
                        className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 mr-1 inline-flex items-center gap-1"
                        data-testid={`readings-${m.id}`}
                      >
                        <Gauge weight="bold" className="w-3.5 h-3.5" /> Ablesungen
                      </button>
                      <button
                        onClick={() => { setEditingMeter(m); setShowMeterModal(true); }}
                        className="p-1.5 text-slate-400 hover:text-slate-700"
                        data-testid={`edit-meter-${m.id}`}
                      >
                        <PencilSimple weight="bold" className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(m)}
                        className="p-1.5 text-slate-400 hover:text-red-600"
                        data-testid={`delete-meter-${m.id}`}
                      >
                        <Trash weight="bold" className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Modals */}
      <MeterModal
        open={showMeterModal}
        meter={editingMeter}
        properties={properties}
        units={units}
        onClose={() => { setShowMeterModal(false); setEditingMeter(null); }}
        onSaved={() => { loadData(); setShowMeterModal(false); setEditingMeter(null); }}
      />
      {openReadings && (
        <ReadingsModal
          meter={openReadings}
          tenants={tenants}
          onClose={() => setOpenReadings(null)}
          onChanged={loadData}
        />
      )}
    </div>
  );
}

function MeterModal({ open, meter, properties, units, onClose, onSaved }) {
  const initial = {
    unit_id: '',
    meter_type: 'strom',
    label: '',
    serial_number: '',
    unit_of_measure: 'kWh',
    conversion_factor: '',
    active: true,
    paid_by_tenant: true,
    installed_at: '',
    removed_at: '',
    notes: '',
  };
  const [formData, setFormData] = useState(initial);
  const [propertyFilter, setPropertyFilter] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (meter) {
      setFormData({
        unit_id: meter.unit_id || '',
        meter_type: meter.meter_type || 'strom',
        label: meter.label || '',
        serial_number: meter.serial_number || '',
        unit_of_measure: meter.unit_of_measure || 'kWh',
        conversion_factor: meter.conversion_factor ?? '',
        active: !!meter.active,
        paid_by_tenant: !!meter.paid_by_tenant,
        installed_at: meter.installed_at || '',
        removed_at: meter.removed_at || '',
        notes: meter.notes || '',
      });
      const u = units.find(uu => uu.id === meter.unit_id);
      setPropertyFilter(u?.property_id || '');
    } else {
      setFormData(initial);
      setPropertyFilter('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meter]);

  const unitsFiltered = propertyFilter ? units.filter(u => u.property_id === propertyFilter) : units;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.unit_id) { alert('Bitte Einheit wählen'); return; }
    if (!formData.label.trim()) { alert('Bitte Bezeichnung eingeben'); return; }
    setSaving(true);
    try {
      const num = (v) => v === '' || v === null ? null : parseFloat(String(v).replace(',', '.'));
      const data = {
        ...formData,
        conversion_factor: num(formData.conversion_factor),
        installed_at: formData.installed_at || null,
        removed_at: formData.removed_at || null,
        notes: formData.notes || null,
        serial_number: formData.serial_number || null,
      };
      if (meter) await updateMeter(meter.id, data);
      else await createMeter(data);
      onSaved();
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="meter-modal">
      <div className="bg-white w-full max-w-xl rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-slate-900">{meter ? 'Zähler bearbeiten' : 'Neuer Zähler'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X weight="bold" className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Objekt">
              <select value={propertyFilter} onChange={(e) => { setPropertyFilter(e.target.value); setFormData(prev => ({ ...prev, unit_id: '' })); }} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none">
                <option value="">— wählen —</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Einheit *">
              <select value={formData.unit_id} onChange={(e) => setFormData(prev => ({ ...prev, unit_id: e.target.value }))} required className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" data-testid="meter-unit-select">
                <option value="">— wählen —</option>
                {unitsFiltered.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Zählertyp *">
              <select
                value={formData.meter_type}
                onChange={(e) => {
                  const t = e.target.value;
                  setFormData(prev => ({ ...prev, meter_type: t, unit_of_measure: prev.unit_of_measure || (METER_TYPE_LABELS[t]?.defaultUnit || '') }));
                }}
                className="w-full border-b border-slate-300 bg-transparent py-2 outline-none"
                data-testid="meter-type-select"
              >
                {Object.entries(METER_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Bezeichnung *">
              <input type="text" value={formData.label} onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))} required placeholder="z.B. Strom Wohnung" className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" data-testid="meter-label-input" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Zählernummer">
              <input type="text" value={formData.serial_number} onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none font-mono" />
            </Field>
            <Field label="Einheit">
              <input type="text" value={formData.unit_of_measure} onChange={(e) => setFormData(prev => ({ ...prev, unit_of_measure: e.target.value }))} placeholder="kWh, m³, …" className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" />
            </Field>
            <Field label="Umrechnung €/Einheit">
              <input type="text" inputMode="decimal" value={formData.conversion_factor} onChange={(e) => setFormData(prev => ({ ...prev, conversion_factor: e.target.value }))} placeholder="0,35" className="w-full border-b border-slate-300 bg-transparent py-2 outline-none font-mono" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Einbau-Datum">
              <input type="date" value={formData.installed_at || ''} onChange={(e) => setFormData(prev => ({ ...prev, installed_at: e.target.value }))} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" />
            </Field>
            <Field label="Ausbau-Datum">
              <input type="date" value={formData.removed_at || ''} onChange={(e) => setFormData(prev => ({ ...prev, removed_at: e.target.value }))} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" />
            </Field>
          </div>
          <div className="flex flex-wrap gap-4 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.active} onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))} data-testid="meter-active-checkbox" />
              <span className="text-sm text-slate-700">Zähler aktiv</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.paid_by_tenant} onChange={(e) => setFormData(prev => ({ ...prev, paid_by_tenant: e.target.checked }))} data-testid="meter-paid-by-tenant-checkbox" />
              <span className="text-sm text-slate-700">Vom Mieter zu zahlen / abzulesen</span>
            </label>
          </div>
          <Field label="Notizen">
            <textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} rows={2} className="w-full border border-slate-200 rounded px-3 py-2 text-sm" />
          </Field>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2.5 rounded hover:bg-slate-50">Abbrechen</button>
            <button type="submit" disabled={saving} className="flex-1 bg-slate-900 text-white py-2.5 rounded hover:bg-slate-800 disabled:opacity-50" data-testid="meter-save-button">
              {saving ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ReadingsModal({ meter, tenants, onClose, onChanged }) {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [newR, setNewR] = useState({ reading_date: today, value: '', reason: 'regular', notes: '' });
  const [periodFrom, setPeriodFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [periodTo, setPeriodTo] = useState(`${new Date().getFullYear()}-12-31`);
  const [consumption, setConsumption] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listMeterReadings(meter.id);
      setReadings(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [meter.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (newR.value === '' || newR.value === null) { alert('Bitte Wert eingeben'); return; }
    try {
      await createMeterReading(meter.id, {
        reading_date: newR.reading_date,
        value: parseFloat(String(newR.value).replace(',', '.')),
        reason: newR.reason,
        notes: newR.notes || null,
      });
      setNewR({ reading_date: today, value: '', reason: 'regular', notes: '' });
      setShowForm(false);
      load();
      if (onChanged) onChanged();
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleVerify = async (id) => {
    try { await verifyMeterReading(id); load(); }
    catch (e) { alert('Fehler: ' + (e.response?.data?.detail || e.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Diese Ablesung wirklich löschen?')) return;
    try { await deleteMeterReading(id); load(); }
    catch (e) { alert('Fehler: ' + (e.response?.data?.detail || e.message)); }
  };

  const computeConsumption = async () => {
    try {
      const res = await getMeterConsumption(meter.id, periodFrom, periodTo);
      setConsumption(res.data);
    } catch (e) {
      alert('Fehler: ' + (e.response?.data?.detail || e.message));
    }
  };

  const tenantName = (tid) => tenants.find(t => t.id === tid)?.name || '—';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-3xl rounded-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">Ablesungen — {meter.label}</h2>
            <p className="text-sm text-slate-500">{(METER_TYPE_LABELS[meter.meter_type] || {}).label} · {meter.unit_of_measure}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X weight="bold" className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-slate-500">Verbrauch von</label>
              <input type="date" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)} className="border-b border-slate-300 bg-transparent py-1 text-sm" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-slate-500">bis</label>
              <input type="date" value={periodTo} onChange={e => setPeriodTo(e.target.value)} className="border-b border-slate-300 bg-transparent py-1 text-sm" />
            </div>
            <button onClick={computeConsumption} className="bg-slate-900 text-white px-3 py-1.5 rounded text-sm inline-flex items-center gap-1.5 hover:bg-slate-800" data-testid="compute-consumption-btn">
              <Calculator weight="bold" className="w-4 h-4" /> Berechnen
            </button>
            {consumption && consumption.consumption != null && (
              <div className="ml-auto text-right text-sm">
                <p className="text-slate-700">Verbrauch: <span className="font-bold font-mono">{consumption.consumption.toFixed(2).replace('.', ',')} {meter.unit_of_measure}</span></p>
                {consumption.cost_estimate != null && (
                  <p className="text-slate-500">≈ {eur(consumption.cost_estimate)}</p>
                )}
              </div>
            )}
            {consumption && consumption.consumption == null && (
              <p className="ml-auto text-sm text-amber-700">{consumption.note}</p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading text-base font-bold text-slate-900">Historie ({readings.length})</h3>
          <button onClick={() => setShowForm(s => !s)} className="bg-slate-900 text-white px-3 py-1.5 rounded text-sm inline-flex items-center gap-1.5 hover:bg-slate-800" data-testid="add-reading-button">
            <Plus weight="bold" className="w-4 h-4" /> Ablesung erfassen
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="px-6 py-4 bg-amber-50 border-b border-amber-200 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
            <Field label="Datum">
              <input type="date" value={newR.reading_date} onChange={e => setNewR({ ...newR, reading_date: e.target.value })} required className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" data-testid="new-reading-date" />
            </Field>
            <Field label={`Wert (${meter.unit_of_measure})`}>
              <input type="text" inputMode="decimal" value={newR.value} onChange={e => setNewR({ ...newR, value: e.target.value })} required placeholder="0,00" className="w-full border-b border-slate-300 bg-transparent py-2 outline-none font-mono" data-testid="new-reading-value" />
            </Field>
            <Field label="Anlass">
              <select value={newR.reason} onChange={e => setNewR({ ...newR, reason: e.target.value })} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" data-testid="new-reading-reason">
                {Object.entries(REASON_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Notiz">
              <input type="text" value={newR.notes} onChange={e => setNewR({ ...newR, notes: e.target.value })} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" />
            </Field>
            <button type="submit" className="bg-slate-900 text-white py-2 rounded hover:bg-slate-800" data-testid="save-reading-button">Speichern</button>
          </form>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div></div>
          ) : readings.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">Noch keine Ablesungen erfasst.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">Datum</th>
                  <th className="text-right px-4 py-2">Wert</th>
                  <th className="text-left px-4 py-2">Anlass</th>
                  <th className="text-left px-4 py-2">Mieter</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {readings.map(r => {
                  const sb = STATUS_BADGES[r.status] || STATUS_BADGES.submitted;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`reading-row-${r.id}`}>
                      <td className="px-4 py-2 text-slate-900">{r.reading_date}</td>
                      <td className="px-4 py-2 text-right font-mono font-medium text-slate-900">{r.value} {meter.unit_of_measure}</td>
                      <td className="px-4 py-2 text-slate-700">{REASON_LABELS[r.reason] || r.reason}</td>
                      <td className="px-4 py-2 text-slate-700">{tenantName(r.tenant_id)}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded border ${sb.cls}`}>{sb.label}</span>
                        {r.warnings?.length > 0 && (
                          <div className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1">
                            <Warning weight="bold" className="w-3.5 h-3.5" /> {r.warnings[0]}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {r.status !== 'verified' && (
                          <button onClick={() => handleVerify(r.id)} className="text-xs px-2 py-1 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 mr-1 inline-flex items-center gap-1" data-testid={`verify-${r.id}`} title="Als geprüft markieren">
                            <CheckCircle weight="bold" className="w-3.5 h-3.5" /> Prüfen
                          </button>
                        )}
                        <button onClick={() => handleDelete(r.id)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`delete-reading-${r.id}`}>
                          <Trash weight="bold" className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
